import React, { Component } from 'react';
import PropTypes from 'prop-types';
import DateNodeTree from '_legacy/tools/DateNodeTree';
import FilterTreeView from '_legacy/components/grid/filters/FilterTreeView';
import { blankKeyText } from '_legacy/constants/gridText';
import { FilterGenerator } from '_legacy/components/grid/filters/FilterGenerator';

const monthToNum = {
    Jan: '01',
    Feb: '02',
    Mar: '03',
    Apr: '04',
    May: '05',
    Jun: '06',
    Jul: '07',
    Aug: '08',
    Sep: '09',
    Oct: '10',
    Nov: '11',
    Dec: '12',
};

const toIsoFormat = (dateStr) => {
    if (!dateStr || typeof dateStr !== 'string') return null;
    const parts = dateStr.trim().split(' ');
    if (parts.length !== 2) return null;
    const month = monthToNum[parts[0]];
    const year = parts[1];
    if (!month || !/^\d{4}$/.test(year)) return null;
    return `${year}-${month}-01`;
};

class BuiltDateFilter extends Component {
    constructor(props) {
        super(props);
        this.handleBlanksChange = this.handleBlanksChange.bind(this);
        this.handleAllDatesChange = this.handleAllDatesChange.bind(this);
        this.clearFilter = this.clearFilter.bind(this);
        this.handleNodeClicked = this.handleNodeClicked.bind(this);
        this.handleNodeToggled = this.handleNodeToggled.bind(this);
        this.collapseTree = this.collapseTree.bind(this);
        this.tree = this.createTree(props.dates);

        this.state = {
            showBlanks: true,
            visibleNodes: props.visibleNodes ? props.visibleNodes : {},
            filterState: this.tree.filterState,
            expanded: [],
        };
    }

    componentDidMount() {
        this.refreshFilterTree();
        this.collapseTree();
    }

    createTree(dates) {
        const stringArray = dates ? [...dates] : [];
        this.props.api.forEachNode((node) => {
            if (node.data) {
                const value = this.getBuiltDate(node);
                if (value) {
                    const isoDate = toIsoFormat(value);
                    if (isoDate) {
                        stringArray.push(isoDate);
                    }
                }
            }
        });
        return new DateNodeTree(stringArray, false, true);
    }

    afterGuiAttached() {
        this.refreshFilterTree();
        this.collapseTree();
    }

    handleNodeToggled(nodes) {
        this.setState({ expanded: nodes });
    }

    collapseTree() {
        this.setState({ expanded: [] });
    }

    refreshFilterTree() {
        const currentFilterState = this.tree.filterState;
        this.tree = this.createTree();
        this.tree.setFilterState(currentFilterState);
        const visibleNodes = {};

        const isRowVisible = this.props.doesRowPassOtherFilter;
        this.props.api.forEachNode((node) => {
            if (node.data && isRowVisible(node)) {
                const value = this.getBuiltDate(node);
                const isoDate = toIsoFormat(value);
                const hierarchy = this.tree.getNodeAncestors(isoDate);
                if (hierarchy) {
                    hierarchy.forEach((i) => (visibleNodes[i] = true));
                }
            }
        });

        this.setState({ filterState: this.tree.filterState, visibleNodes });
    }

    handleBlanksChange(_, checked) {
        this.setState(
            {
                filterState: this.tree.filterState,
                showBlanks: checked,
            },
            () => {
                this.refreshFilterTree();
                this.props.filterChangedCallback();
            }
        );
    }

    handleAllDatesChange(_, checked) {
        const state = checked
            ? DateNodeTree.States.CHECKED
            : DateNodeTree.States.UNCHECKED;
        this.tree.setCheckbox('_', state);

        this.setState({ filterState: this.tree.filterState }, () => {
            this.refreshFilterTree();
            this.props.filterChangedCallback();
        });
    }

    handleNodeClicked(treeNode, checked) {
        let { showBlanks } = this.state;

        this.tree.setCheckbox(
            treeNode.key,
            checked
                ? DateNodeTree.States.CHECKED
                : DateNodeTree.States.UNCHECKED
        );

        this.setState({ showBlanks, filterState: this.tree.filterState }, () =>
            this.props.filterChangedCallback()
        );
    }

    clearFilter() {
        this.tree.clearFilter();
        this.setState(
            {
                showBlanks: true,
                filterState: this.tree.filterState,
            },
            () => {
                this.refreshFilterTree();
                this.props.filterChangedCallback();
            }
        );
    }

    getBuiltDate(node) {
        const data = node.data;
        if (data && data.vessel && data.vessel.builtDate) {
            return data.vessel.builtDate;
        }
        return null;
    }

    isShowingAllDates = () => {
        return this.tree.filterState.dateFilterSet.includes(
            DateNodeTree.RootNodeKey
        );
    };

    isFilterActive() {
        return !this.state.showBlanks || !this.isShowingAllDates();
    }

    doesFilterPass(params) {
        const { showBlanks } = this.state;
        const builtDate = this.getBuiltDate(params.node);

        if (!builtDate && showBlanks) {
            return true;
        }

        if (builtDate && this.isShowingAllDates()) {
            return true;
        }

        if (builtDate) {
            const isoDate = toIsoFormat(builtDate);
            return this.tree.isChecked(isoDate);
        }

        return false;
    }

    getModel() {
        const { filterState, showBlanks } = this.state;

        return this.isShowingAllDates() && showBlanks
            ? null
            : { filterState, showBlanks };
    }

    setModel(model) {
        if (model) {
            this.tree.setFilterState(model.filterState);
            this.setState(model, () => this.props.filterChangedCallback());
        } else {
            this.clearFilter();
        }
    }

    render() {
        const visibleNodes = this.state.visibleNodes;

        const elements = [
            [
                {
                    type: 'checkbox',
                    checked: this.state.showBlanks,
                    onChange: this.handleBlanksChange,
                    label: blankKeyText,
                },
                {
                    type: 'checkbox',
                    checked: this.isShowingAllDates(),
                    onChange: this.handleAllDatesChange,
                    label: 'ALL DATES',
                },
            ],
            <FilterTreeView
                tree={this.tree}
                visibleNodes={visibleNodes}
                expanded={this.state.expanded}
                onNodeClicked={this.handleNodeClicked}
                onNodeToggle={this.handleNodeToggled}
            />,
        ];

        return <FilterGenerator elements={elements} clear={this.clearFilter} />;
    }
}

BuiltDateFilter.propTypes = {
    filterChangedCallback: PropTypes.func.isRequired,
};

export default BuiltDateFilter;
